import React, {useState, ReactElement} from 'react';

import moment from 'moment';

import {SvgIcon} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/material/styles';

import AnalyticsTotals from './AnalyticsTotals';
import ItemsSoldChart from './ItemsSoldChart';
import PageViewChart from './PageViewChart';
import SalesByChart from './SalesByChart';
import ProductAnalyticsTable from './ProductAnalyticsTable';
import {
  useGetStoreAnalyticsDataQuery,
  useGetStoreAnalyticsProductsQuery
} from '../../api/storesApi';

import './index.scss';

import PrintIcon from '../../assets/icons/print.svg?com';

type StoreAnalyticsProps = {
  storeSlug: string;
};

const StoreAnalytics: React.FC<StoreAnalyticsProps> = ({
  storeSlug
}: StoreAnalyticsProps): ReactElement => {
  const chartStyles = {
    animationDuration: 300,
    lineType: 'monotoneX',
    strokeWidth: 2
  };

  const timeframes = [
    {
      value: 'day',
      date_start: moment().subtract(1, 'days').format(),
      date_end: moment().format(),
      bucket_size: 'hour',
      display: 'Past 24 Hours'
    },
    {
      value: 'week',
      date_start: moment().subtract(7, 'days').format(),
      date_end: moment().format(),
      display: 'Past 7 Days'
    },
    {
      value: 'twoWeek',
      date_start: moment().subtract(14, 'days').format(),
      date_end: moment().format(),
      display: 'Past 2 Weeks'
    },
    {
      value: 'month',
      date_start: moment().subtract(30, 'days').format(),
      date_end: moment().format(),
      display: 'Past Month'
    },
    {
      value: 'threeMonth',
      date_start: moment().subtract(90, 'days').format(),
      date_end: moment().format(),
      display: 'Past 3 Months'
    }
  ];

  const [timeframe, setTimeframe] = useState(
    timeframes.find(tf => tf.value === 'twoWeek')
  );

  const {
    data,
    isLoading: loadingAnalytics,
    isFetching: fetchingAnalytics
  } = useGetStoreAnalyticsDataQuery({
    urlSlug: storeSlug,
    queryParams: {
      date_start: timeframe.date_start,
      date_end: timeframe.date_end,
      bucket_size: 'day'
    }
  });

  const loadingAnalyticsData = loadingAnalytics || fetchingAnalytics;

  const {
    data: productsData,
    isLoading: loadingProducts,
    isFetching: fetchingProducts
  } = useGetStoreAnalyticsProductsQuery({
    urlSlug: storeSlug,
    queryParams: {
      date_start: timeframe.date_start,
      date_end: timeframe.date_end,
      bucket_size: 'day'
    }
  });

  const loadingProductsData = loadingProducts || fetchingProducts;

  const renderTimeFrameSelect = () => {
    return (
      <select
        name="time"
        id="time"
        className="time-select"
        defaultValue="twoWeek"
        onChange={e =>
          setTimeframe(timeframes.find(tf => tf.value === e.target.value))
        }>
        {timeframes.map(tf => {
          return (
            <option value={tf.value} key={tf.value}>
              {tf.display}
            </option>
          );
        })}
      </select>
    );
  };

  const renderAnalyticsHeader = (): ReactElement => {
    return (
      <>
        <h2 className="admin-StoreAnalytics-printName">Analytics</h2>
        <div className="flex-row analytics-header">
          <span className="time-label">duration</span>
          {renderTimeFrameSelect()}
          <div className="align-right">
            <button
              type="button"
              className="admin-StoreAnalytics-print"
              onClick={() => window.print()}>
              <SvgIcon
                viewBox="0 0 22 19"
                htmlColor="#1e39d2"
                component={PrintIcon}
              />
              <span>Printer-Friendly Version</span>
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderChartsDesktop = () => {
    return (
      <div className="admin-StoreAnalytics-charts charts-left flex-row spaced-charts">
        <SalesByChart
          height="350px"
          chartStyles={chartStyles}
          data={data?.sales}
          loading={loadingAnalyticsData}
        />
        <div className="charts-right flex-column spaced-charts">
          <ItemsSoldChart
            chartStyles={chartStyles}
            data={data?.sales}
            loading={loadingAnalyticsData}
          />
          <PageViewChart
            chartStyles={chartStyles}
            data={data?.traffic}
            loading={loadingAnalyticsData}
          />
        </div>
      </div>
    );
  };

  const renderChartsMobile = () => {
    return (
      <div className="admin-StoreAnalytics-charts flex-column spaced-charts">
        <SalesByChart
          height="200px"
          chartStyles={chartStyles}
          data={data?.sales}
          loading={loadingAnalyticsData}
        />
        <ItemsSoldChart
          height="200px"
          chartStyles={chartStyles}
          data={data?.sales}
          loading={loadingAnalyticsData}
        />
        <PageViewChart
          height="200px"
          chartStyles={chartStyles}
          data={data?.traffic}
          loading={loadingAnalyticsData}
        />
      </div>
    );
  };

  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div className="analytics-container">
      {renderAnalyticsHeader()}
      <AnalyticsTotals data={data} loading={loadingAnalyticsData} />
      {mobileView ? renderChartsMobile() : renderChartsDesktop()}
      <ProductAnalyticsTable
        data={productsData}
        loading={loadingProductsData}
      />
    </div>
  );
};

export default StoreAnalytics;
